import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheck, Clock, Award, Camera, X, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  
  const PHOTO_TYPES = [
    { id: 'front', label: t('hero.photoFront') },
    { id: 'angle45', label: t('hero.photoAngle') },
    { id: 'side', label: t('hero.photoSide') },
    { id: 'back', label: t('hero.photoBack') }
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [photos, setPhotos] = useState({
    front: null,
    angle45: null,
    side: null,
    back: null
  });
  const [photoPreviews, setPhotoPreviews] = useState({
    front: null,
    angle45: null,
    side: null,
    back: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRefs = {
    front: useRef(null),
    angle45: useRef(null),
    side: useRef(null),
    back: useRef(null)
  };

  const handlePhotoChange = (photoType, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'en' ? 'Image too large. Max 5MB.' : 'La imagen es muy grande. Máximo 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotos(prev => ({ ...prev, [photoType]: reader.result }));
        setPhotoPreviews(prev => ({ ...prev, [photoType]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (photoType) => {
    setPhotos(prev => ({ ...prev, [photoType]: null }));
    setPhotoPreviews(prev => ({ ...prev, [photoType]: null }));
    if (fileInputRefs[photoType].current) {
      fileInputRefs[photoType].current.value = '';
    }
  };

  const hasAtLeastOnePhoto = () => {
    return Object.values(photos).some(photo => photo !== null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasAtLeastOnePhoto()) {
      toast.error(language === 'en' ? 'Please upload at least one photo.' : 'Por favor sube al menos una fotografía.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('service', formData.service);
      formDataObj.append('message', formData.message || '');

      Object.entries(photos).forEach(([type, file]) => {
        if (file) {
          formDataObj.append('photos', file, `${type}_${file.name}`);
        }
      });

      await axios.post(`${API}/contact`, formDataObj);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setPhotos({ front: null, angle45: null, side: null, back: null });
      setPhotoPreviews({ front: null, angle45: null, side: null, back: null });
      const thankYouPath = language === 'en' ? '/en/gracias' : '/gracias';
      navigate(thankYouPath);
    } catch (error) {
      toast.error(language === 'en' ? 'Error submitting form. Please try again.' : 'Error al enviar el formulario. Por favor intenta de nuevo.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="hero-gradient pt-28 pb-16 md:pt-36 md:pb-24" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-sm font-semibold tracking-wider uppercase text-[#009d9f]">
                {t('hero.tagline')}
              </p>
              <h1 className="sm:text-5xl lg:text-6xl !font-bold !text-6xl !leading-[4.0rem] text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('hero.title')}{' '}
                <span className="!text-5xl !leading-[2.5rem] text-[#009d9f]">{t('hero.subtitle')}</span>
              </h1>
              <p className="md:text-xl max-w-xl !text-lg text-slate-600">
                {t('hero.description')}
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4" data-testid="trust-badges">
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-100">
                <ShieldCheck className="w-5 h-5 text-[#009d9f]" />
                <span className="text-sm font-medium text-slate-700">{t('hero.badge1')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-100">
                <Clock className="w-5 h-5 text-[#009d9f]" />
                <span className="text-sm font-medium text-slate-700">{t('hero.badge2')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-100">
                <Award className="w-5 h-5 text-[#009d9f]" />
                <span className="text-sm font-medium text-slate-700">{t('hero.badge3')}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center">
                <p className="md:text-4xl !font-bold !text-3xl text-[#009d9f]" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('hero.stat1')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('hero.stat1Label')}</p>
              </div>
              <div className="text-center">
                <p className="md:text-4xl !font-bold !text-3xl text-[#009d9f]" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('hero.stat2')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('hero.stat2Label')}</p>
              </div>
              <div className="text-center">
                <p className="md:text-4xl !font-bold !text-3xl text-[#009d9f]" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('hero.stat3')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('hero.stat3Label')}</p>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-6 md:p-8 rounded-3xl shadow-2xl border border-slate-100" data-testid="hero-contact-form">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('hero.formTitle')}
              </h2>
              <p className="text-slate-500 mt-2">{t('hero.formSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                  {t('hero.formName')} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'en' ? 'Your name' : 'Tu nombre'}
                  required
                  className="bg-slate-50 border-slate-200 focus:border-[#009d9f] focus:ring-[#009d9f]/20 rounded-xl h-12"
                  data-testid="form-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                    {t('hero.formEmail')} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={language === 'en' ? 'your@email.com' : 'tu@email.com'}
                    required
                    className="bg-slate-50 border-slate-200 focus:border-[#009d9f] focus:ring-[#009d9f]/20 rounded-xl h-12"
                    data-testid="form-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                    {t('hero.formPhone')} *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+52 664 123 4567"
                    required
                    className="bg-slate-50 border-slate-200 focus:border-[#009d9f] focus:ring-[#009d9f]/20 rounded-xl h-12"
                    data-testid="form-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="service" className="text-sm font-semibold text-slate-700 mb-2 block">
                  {t('hero.formService')} *
                </Label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, service: value }))}
                  required
                >
                  <SelectTrigger
                    className="bg-slate-50 border-slate-200 focus:border-[#009d9f] focus:ring-[#009d9f]/20 rounded-xl h-12"
                    data-testid="form-service-select"
                  >
                    <SelectValue placeholder={t('hero.formServicePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trasplante-capilar">{t('serviceOptions.trasplante-capilar')}</SelectItem>
                    <SelectItem value="injerto-barba">{t('serviceOptions.injerto-barba')}</SelectItem>
                    <SelectItem value="injerto-mujeres">{t('serviceOptions.injerto-mujeres')}</SelectItem>
                    <SelectItem value="injerto-cejas">{t('serviceOptions.injerto-cejas')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Photo Upload Section */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                  <Camera className="w-4 h-4 inline mr-1" />
                  {t('hero.formPhotos')} *
                </Label>
                <p className="text-xs text-slate-500 mb-3">
                  {t('hero.formPhotosDesc')}
                </p>
                
                <div className="grid grid-cols-4 gap-2" data-testid="photo-upload-section">
                  {PHOTO_TYPES.map((photoType) => (
                    <div key={photoType.id} className="relative">
                      <input
                        type="file"
                        ref={fileInputRefs[photoType.id]}
                        accept="image/*"
                        onChange={(e) => handlePhotoChange(photoType.id, e)}
                        className="hidden"
                        id={`photo-${photoType.id}`}
                      />
                      
                      {photoPreviews[photoType.id] ? (
                        <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#009d9f]">
                          <img
                            src={photoPreviews[photoType.id]}
                            alt={photoType.label}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(photoType.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">
                            {photoType.label}
                          </span>
                        </div>
                      ) : (
                        <label
                          htmlFor={`photo-${photoType.id}`}
                          className="aspect-square rounded-xl border-2 border-dashed border-slate-200 hover:border-[#009d9f] bg-slate-50 hover:bg-[#009d9f]/5 cursor-pointer flex flex-col items-center justify-center transition-all"
                        >
                          <Upload className="w-5 h-5 text-slate-400 mb-1" />
                          <span className="text-[10px] text-slate-500 font-medium">{photoType.label}</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-2 block">
                  {t('hero.formMessage')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('hero.formMessagePlaceholder')}
                  rows={3}
                  className="bg-slate-50 border-slate-200 focus:border-[#009d9f] focus:ring-[#009d9f]/20 rounded-xl resize-none"
                  data-testid="form-message-textarea"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#009d9f] hover:bg-[#008486] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-[#009d9f]/20 transition-all hover:-translate-y-0.5"
                data-testid="form-submit-button"
              >
                {isSubmitting ? t('hero.formSubmitting') : t('hero.formSubmit')}
              </Button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-4">
              {t('hero.formDisclaimer')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
